import { useCallback, useState } from 'react';
import { nextSort, sortRows, type SortState, type SortValue } from './table-sort';

/**
 * Sort state for one table, with the app-wide click rule
 * (`app-kit/table-sort.ts`).
 *
 * `initial` is the table's default order: name a visible column to show it as
 * sorted from the start, or `{ key: null, dir: 'asc' }` when the default order is
 * on nothing the table displays. `dateKeys` are the columns that open
 * newest-first. `onChange` runs on every click — a paged list resets to page 1
 * there, because a new order is a different set of rows on every page.
 */
export function useTableSort<K extends string>(
  initial: SortState<K>,
  dateKeys: readonly K[] = [],
  onChange?: (next: SortState<K>) => void,
) {
  const [sort, setSort] = useState<SortState<K>>(initial);
  // ⚠️ `onChange` runs HERE, not inside a `setSort` updater: React may call an
  // updater twice (StrictMode does, in dev), which would reset a page twice.
  const toggle = useCallback((key: K) => {
    const next = nextSort(sort, key, (k) => dateKeys.includes(k));
    setSort(next);
    onChange?.(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dateKeys is a literal at every call site
  }, [sort, onChange]);
  return { sort, toggle };
}

/**
 * Sort state plus the sorted rows, for a table that HOLDS EVERY ROW of its
 * result (`table-sort.ts` — a paged or capped list sorts on the server).
 *
 * Spread `rows`, `sort` and `onSort` into a `DataTable` and give each sortable
 * column a `sortKey`; a hand-written table uses `sort` + `toggle` with `SortTh`.
 * Only keys present in `accessors` are sortable, so a stray key is ignored.
 *
 * Re-sorts on every render: these tables are small, and memoising on an
 * inline `accessors` object would either never hit or go stale.
 */
export function useSortedRows<T, K extends string>(
  rows: readonly T[] | undefined,
  accessors: Record<K, (row: T) => SortValue>,
  // NoInfer: the column set comes from `accessors` alone, so a typo here is an
  // error rather than a new column.
  initial: SortState<NoInfer<K>> = { key: null, dir: 'asc' },
  dateKeys: readonly NoInfer<K>[] = [],
) {
  const { sort, toggle } = useTableSort<K>(initial, dateKeys);
  const onSort = useCallback((key: string) => {
    if (Object.hasOwn(accessors, key)) toggle(key as K);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the KEYS of accessors never change
  }, [toggle]);
  return {
    rows: rows ? sortRows(rows, sort, accessors) : undefined,
    sort,
    toggle,
    onSort,
  };
}
